import React, { useState } from "react";
import { C, IMGS, PORTFOLIO, TESTIMONIALS, WORK_CATS } from "../constants";
import { Reveal, Eyebrow, H, P, Btn, Chip, Sec } from "../components/UI";

function WorkItem({ item, i }) {
  const [hov, setHov] = useState(false);
  return (
    <Reveal delay={i * 80}>
      <div
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ position: "relative", overflow: "hidden", background: "#e0e0e0", cursor: "pointer" }}
      >
        <div style={{ paddingBottom: "75%", backgroundImage: `url(${item.img})`, backgroundSize: "cover", backgroundPosition: "center", transform: hov ? "scale(1.07)" : "scale(1)", transition: "transform 0.72s cubic-bezier(.4,0,.2,1)" }} />
        <div style={{ position: "absolute", inset: 0, background: hov ? "rgba(10,10,10,0.74)" : "rgba(10,10,10,0.32)", transition: "background 0.38s ease", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "24px 28px 28px" }}>
          <div style={{ transform: hov ? "translateY(0)" : "translateY(6px)", opacity: hov ? 1 : 0.82, transition: "all 0.34s" }}>
            <Chip>{item.cat}</Chip>
            <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "clamp(16px,2vw,22px)", color: C.white, margin: "10px 0 5px", letterSpacing: -0.3 }}>{item.title}</h4>
            <P size="sm" color="rgba(255,255,255,0.55)">{item.client}</P>
            {hov && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
                <span style={{ width: 24, height: 2, background: C.accent }} />
                <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 11, color: C.accent, letterSpacing: 2, textTransform: "uppercase" }}>View Project</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", padding: "4px 12px" }}>
          <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 12, color: C.white, letterSpacing: 1 }}>{item.year}</span>
        </div>
      </div>
    </Reveal>
  );
}

function TestimonialCard({ t, i }) {
  return (
    <Reveal delay={i * 100}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, padding: "36px 32px", height: "100%" }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 36, color: C.accent, lineHeight: 1, marginBottom: 14, fontWeight: 900 }}>"</div>
        <P size="sm" color={C.charcoal} style={{ marginBottom: 24, fontStyle: "italic", lineHeight: 1.9 }}>{t.quote}</P>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.accentLight, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 15, color: C.accent, flexShrink: 0 }}>{t.name[0]}</div>
          <div>
            <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 13, color: C.black }}>{t.name}</div>
            <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 12, color: C.muted }}>{t.company}</div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function WorkPage({ setPage }) {
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter);

  return (
    <div style={{ paddingTop: 84 }}>
      {/* Hero */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: 480, display: "flex", alignItems: "flex-end", background: C.black }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMGS.camera})`, backgroundSize: "cover", backgroundPosition: "center 40%", opacity: 0.42 }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(10,10,10,0.96) 38%, rgba(10,10,10,0.35) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,10,10,0.9) 0%, transparent 60%)" }} />
        <div style={{ position: "relative", padding: "0 clamp(20px,7vw,120px) 80px", width: "100%" }}>
          <Reveal>
            <Eyebrow text="Portfolio" />
            <H size="xl" color={C.white} style={{ marginTop: 12 }}>Stories We've<br />Brought to Life</H>
            <P size="lg" color="rgba(255,255,255,0.5)" style={{ marginTop: 18, maxWidth: 460, lineHeight: 1.85 }}>
              A curated selection of projects where strategy met storytelling — and performance followed.
            </P>
          </Reveal>
        </div>
      </section>

      {/* Filter Bar */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "0 clamp(20px,7vw,120px)", position: "sticky", top: 66, zIndex: 100, overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 0, minWidth: "max-content" }}>
          {WORK_CATS.map((cat) => (
            <button key={cat} onClick={() => setFilter(cat)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: filter === cat ? C.accent : C.muted, borderBottom: `2px solid ${filter === cat ? C.accent : "transparent"}`, padding: "17px 18px", whiteSpace: "nowrap", transition: "all 0.22s" }}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Portfolio Grid */}
      <Sec bg={C.surface}>
        <div className="tp-3col" style={{ gap: 3 }}>
          {visible.map((item, i) => <WorkItem key={item.id} item={item} i={i} />)}
        </div>
        {visible.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <P size="lg" color={C.muted}>No projects in this category yet — check back soon.</P>
          </div>
        )}
      </Sec>

      {/* Testimonials */}
      <Sec bg={C.white}>
        <Reveal>
          <Eyebrow text="Client Love" />
          <H size="lg" style={{ marginBottom: 52 }}>What Our Clients Say</H>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 3 }}>
          {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} t={t} i={i} />)}
        </div>
      </Sec>

      {/* CTA Strip */}
      <section style={{ background: C.accent, padding: "90px clamp(20px,7vw,120px)", display: "flex", alignItems: "center", gap: 48, flexWrap: "wrap", justifyContent: "space-between" }}>
        <Reveal>
          <H size="lg" color={C.white} style={{ maxWidth: 540 }}>Want to Be in Our Next Portfolio?</H>
        </Reveal>
        <Reveal delay={80}>
          <Btn variant="solid" onClick={() => { setPage("contact"); window.scrollTo(0, 0); }} style={{ background: C.white, borderColor: C.white, color: C.accent }}>
            Start Your Project
          </Btn>
        </Reveal>
      </section>
    </div>
  );
}
